import React from 'react';
import { Heart, Server, FileText, Languages, CreditCard, Building2, AlertCircle } from 'lucide-react';

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <Heart className="h-16 w-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-[#064635] mb-4">
            Support This Archive
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Your support helps preserve E.S.V. Tomori's scholarly works and makes them freely
            accessible to researchers, students, and anyone interested in Yoruba history and culture.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-[#064635] mb-6">What Your Donation Supports</h2>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <FileText className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#064635] mb-1">PDF Digitization</h3>
                <p className="text-gray-600">
                  Scanning, cleaning, and digitizing physical publications to create high-quality,
                  searchable PDFs for long-term preservation and accessibility.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Server className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#064635] mb-1">Server & Hosting Costs</h3>
                <p className="text-gray-600">
                  Maintaining reliable servers and database infrastructure to ensure the archive
                  remains accessible 24/7 to users worldwide.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Languages className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#064635] mb-1">Yoruba Translation</h3>
                <p className="text-gray-600">
                  Supporting translation efforts to make these works accessible in Yoruba language,
                  ensuring cultural authenticity and wider reach.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Heart className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-[#064635] mb-1">Research Assistance</h3>
                <p className="text-gray-600">
                  Funding research assistants who help catalog, organize, and verify the accuracy
                  of materials in the archive.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-[#064635] mb-6">How to Donate</h2>

          <div className="space-y-6">
            <div className="border border-[#D4AF37] rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="h-6 w-6 text-[#D4AF37]" />
                <h3 className="text-xl font-semibold text-[#064635]">Bank Transfer (Nigeria)</h3>
              </div>
              <div className="space-y-2 text-gray-700">
                <p className="flex justify-between border-b border-gray-200 pb-2">
                  <span className="font-medium">Bank Name:</span>
                  <span>First Bank of Nigeria</span>
                </p>
                <p className="flex justify-between border-b border-gray-200 pb-2">
                  <span className="font-medium">Account Name:</span>
                  <span>MACOS Consultancy</span>
                </p>
                <p className="flex justify-between border-b border-gray-200 pb-2">
                  <span className="font-medium">Account Number:</span>
                  <span className="font-mono">1234567890</span>
                </p>
                <p className="flex justify-between">
                  <span className="font-medium">Account Type:</span>
                  <span>Savings</span>
                </p>
              </div>
              <p className="text-sm text-gray-600 mt-4 italic">
                Please include "Echoes of Ibadan Archive" in your transfer reference so we can properly
                acknowledge your contribution.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <CreditCard className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Online Payment Integration</h3>
                  <p className="text-blue-800 mb-3">
                    We are working to integrate online payment options including credit/debit cards and
                    international payment methods for easier contributions from donors worldwide.
                  </p>
                  <p className="text-sm text-blue-700">
                    Expected launch: Q2 2025
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">International Donors</h3>
                  <p className="text-amber-800">
                    If you're outside Nigeria and would like to support the archive, please contact us at{' '}
                    <a href="mailto:lolatomori@yahoo.com" className="text-[#064635] font-medium hover:underline">
                      lolatomori@yahoo.com
                    </a>
                    {' '}to arrange an international transfer or discuss alternative donation methods.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#064635] text-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-semibold mb-4">Every Contribution Matters</h2>
          <p className="text-lg mb-6">
            Whether large or small, your donation directly supports the preservation of Yoruba
            intellectual heritage and ensures these important works remain accessible to future
            generations of scholars, students, and cultural enthusiasts.
          </p>
          <a
            href="mailto:lolatomori@yahoo.com?subject=Support%20for%20Echoes%20of%20Ibadan%20Archive"
            className="inline-block bg-[#D4AF37] text-[#064635] px-8 py-3 rounded-lg font-semibold
                     hover:bg-opacity-90 transition-colors duration-200"
          >
            Contact Us About Donating
          </a>
        </div>
      </div>
    </div>
  );
}
